import React, { useMemo, useState } from "react";
import CustomModal from "../../CustomModal/CustomModal";
import { Accordion, Image, Stack } from "react-bootstrap";

import "./HowItWorksModal.scss";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import ImageFacePile from "../../../images/face-pile-random.png";
import HyperMiniIcon from "../../icons/HyperMiniIcon";
import ArrowRightIcon, {
  ArrowRightIconVariants,
} from "../../icons/ArrowRightIcon";
import XIcon from "../../icons/XIcon";
import { useTranslation } from "react-i18next";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

function useTranslatedCopy() {
  const { t } = useTranslation("friends");
  const { isFiatDisplay } = useHprFiatFormatter();

  const howItWorks = useMemo(
    () => [
      t("how_it_works.create_a_game_link"),
      t("how_it_works.share_your_game_link_with_friends"),
      t("how_it_works.battle_each_other_may_the_best_player_win"),
    ],
    [t]
  );

  const entryFeeLockExplanation = isFiatDisplay
    ? t(
        "rules.whenever_your_game_link_is_played_we_will_lock_the_entry_fee_fiat"
      )
    : t(
        "rules.whenever_your_game_link_is_played_we_will_lock_the_entry_fee_hpr"
      );

  const rules = useMemo(
    () => [
      entryFeeLockExplanation,
      t(
        "rules.play_your_turn_within_24_hours_or_your_friend_becomes_the_winner"
      ),
      isFiatDisplay
        ? t(
            "rules.if_your_wallet_balance_is_low_your_friends_might_not_be_able_to_challenge_you"
          )
        : t(
            "rules.if_you_are_out_of_hpr_your_friends_might_not_be_able_to_challenge_you"
          ),
      isFiatDisplay
        ? t(
            "rules.if_you_decline_a_created_challenge_30_percent_of_your_entry_fee_would_be_charged_as_a_penalty_fee"
          )
        : t(
            "rules.if_you_decline_a_created_challenge_30_percent_of_your_stake_would_be_charged_as_a_penalty_fee"
          ),
    ],
    [t, isFiatDisplay, entryFeeLockExplanation]
  );

  const faqs = useMemo(
    () => [
      {
        question: t("faqs.how_do_i_create_a_game_link"),
        answer: t("faqs.navigate_to_challenges_and_click_on_play_with_friends"),
      },
      {
        question: t("faqs.how_do_i_get_friends_to_play_my_link"),
        answer: t(
          "faqs.share_your_game_link_to_your_friends_on_the_hyper_community_or_social_media"
        ),
      },
      {
        question: t("faqs.why_am_i_debited_before_i_play"),
        answer: entryFeeLockExplanation,
      },
      {
        question: t(
          "faqs.what_happens_when_there_is_a_network_issue_during_gameplay"
        ),
        answer: t(
          "faqs.you_can_replay_a_game_within_30_minutes_after_interruption"
        ),
      },
      {
        question: t("faqs.why_am_i_penalised_for_declining_games"),
        answer: t("faqs.to_promote_a_fair_gameplay"),
      },
      {
        question: t("faqs.do_i_need_to_create_a_new_game_link_every_time"),
        answer: t("faqs.no_your_game_links_do_not_expire"),
      },
    ],
    [t, entryFeeLockExplanation]
  );

  return {
    faqs,
    howItWorks,
    rules,
  };
}

function Faqs() {
  const [activeEventKey, setActiveEventKey] = useState<string>("0");
  const { faqs } = useTranslatedCopy();

  const toggleFaqs = (eventKey: string) => {
    eventKey === activeEventKey
      ? setActiveEventKey("")
      : setActiveEventKey(eventKey);
  };

  return (
    <Accordion
      defaultActiveKey="0"
      className="d-flex flex-column gap-3 FaqAccordion"
    >
      {faqs.map((faq, index) => {
        const eventKey = `${index}`;
        return (
          <Accordion.Item
            key={faq.question}
            eventKey={eventKey}
            className="d-flex flex-column gap-1 FaqItem"
          >
            <Accordion.Header
              className="Grotesk"
              onClick={() => toggleFaqs(eventKey)}
            >
              {faq.question}
              <p className="p-1">
                <ArrowRightIcon
                  variant={ArrowRightIconVariants.THIN}
                  stroke={eventKey === activeEventKey ? "#6C53E8" : "black"}
                  width={9.3}
                  className="FaqIcon"
                />
              </p>
            </Accordion.Header>
            <Accordion.Body className="Answer">{faq.answer}</Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}

export default function HowItWorksModal({
  isOpen,
  onClose,
}: GenericModalProps) {
  const { t } = useTranslation("friends");
  const { howItWorks, rules } = useTranslatedCopy();

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      isFullscreen
      showCloseBtn={false}
      customClass="HowItWorksModal"
    >
      <div className="d-flex justify-content-center Header">
        <button
          aria-label={t("close_dialog")}
          onClick={onClose}
          className="CloseButton"
        >
          <XIcon bgColor="#F5F5F5" strokeColor="#434343" onClick={onClose} />
        </button>
        <Image src={ImageFacePile} alt={t("avatar_faces")} width={237} />
      </div>

      <Stack gap={4} className="Body">
        <Stack className="Overview">
          <h2 className="Hanson-700 Title">{t("overview")}</h2>
          <p className="Grotesk Content">{t("play_1v1_battle_with_friends")}</p>
        </Stack>
        <Stack className="HowItWorks">
          <h3 className="Grotesk Title">{t("how_it_works.how_it_works")}</h3>
          <Stack gap={2} className="Grotesk Content">
            <Stack as="ol">
              {howItWorks.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Stack>
            <p className="ExtraInfo">
              {t(
                "how_it_works.you_can_create_a_free_game_link_or_stake_an_entry_fee"
              )}
            </p>
          </Stack>
        </Stack>
        <Stack className="Rules">
          <h3 className="Grotesk Title">{t("rules.rules")}</h3>
          <Stack as="ul" gap={3} className="Content">
            {rules.map((item) => (
              <li key={item} className="Rule">
                <span className="ListImage">
                  <HyperMiniIcon />
                </span>
                {item}
              </li>
            ))}
          </Stack>
        </Stack>
        <Stack className="Faqs">
          <h3 className="Grotesk Title">{t("faqs.faqs")}</h3>
          <Faqs />
        </Stack>
      </Stack>
    </CustomModal>
  );
}
